import type {
  AccessListItem,
  AccessListName,
  AccessRowTypes,
} from '@app/application/user-management/types'
import type {
  AccessListView,
  AccessRow,
} from '@app/application/user-management/utils/access-lists'
import type { ComputedRef } from 'vue'

import { computed, reactive } from 'vue'

import { accessCount } from '@app/application/user-management/utils/access-lists'
import { useUserPanelStore } from '@app/application/user-management/stores/user-panel'

// Everything an `AccessList` reads from and sends to a store for one list.
// It is reactive, so a template reads `binding.items` without `.value`.
export interface AccessListBinding<T extends AccessRow> {
  items: AccessListItem[]
  paging: AccessListView<T>
  count: number | string
  search: (query: string) => Promise<void>
  loadMore: () => Promise<void>
}

export interface AccessListSource<T extends AccessRow> {
  view: () => AccessListView<T>
  search: (query: string) => Promise<void>
  loadMore: () => Promise<void>
}

// The binding's fields before `reactive` unwraps them, so a caller can add
// fields of its own.
interface AccessListFields<T extends AccessRow> {
  items: ComputedRef<AccessListItem[]>
  paging: ComputedRef<AccessListView<T>>
  count: ComputedRef<number | string>
  search: (query: string) => Promise<void>
  loadMore: () => Promise<void>
}

export const accessListFields = <T extends AccessRow>(
  source: AccessListSource<T>,
  toItems: (rows: T[]) => AccessListItem[]
): AccessListFields<T> => {
  const paging = computed(source.view)

  return {
    items: computed(() => toItems(paging.value.rows)),
    paging,
    count: computed(() => accessCount(paging.value)),
    search: source.search,
    loadMore: source.loadMore,
  }
}

export default function useAccessList<L extends AccessListName>(
  name: L,
  toItems: (rows: AccessRowTypes[L][]) => AccessListItem[]
): AccessListBinding<AccessRowTypes[L]> {
  const store = useUserPanelStore()

  return reactive(
    accessListFields<AccessRowTypes[L]>(
      {
        view: () => store.accessViews[name],
        search: (query) => store.searchAccess(name, query),
        loadMore: () => store.loadMoreAccess(name),
      },
      toItems
    )
  )
}
